import { useEffect } from 'react'

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-bold text-aubergine">{title}</h2>
      {children}
    </div>
  )
}

function SubSection({ title, children }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-bold text-gray-800">{title}</h3>
      {children}
    </div>
  )
}

export default function TermsAndConditions() {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [])

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-aubergine mb-2">Terms &amp; Conditions of Use</h1>
      <p className="text-sm text-gray-500 mb-8">Last Updated: January 09, 2026</p>

      <div className="prose prose-gray max-w-none space-y-8 text-sm leading-relaxed text-gray-700">
        {/* Acceptance of Terms */}
        <Section title="Acceptance of Terms">
          <p className="font-semibold">Please read the terms of use thoroughly and carefully.</p>

          <p>
            The terms and conditions set forth below (&quot;Terms of Use&quot;) and the Privacy Policy (as defined below) constitute a legally-binding agreement between KidEx Venture Private Limited
            (&quot;PlaceRep.ai&quot;) and you. These Terms of Use contain provisions that define your limits, legal rights and obligations with respect to your use of and participation in (i) the
            PlaceRep.ai app and mobile application, including the classified advertisements, forums, various email functions and Internet links, and all content and PlaceRep.ai services available
            through the domain and sub-domains of PlaceRep.ai located at www.placerep.ai (collectively referred to herein as the &quot;App&quot;), and (ii) the online transactions between those users
            of the App who are offering services (each, a &quot;Service Provider&quot;) and those users of the App who are obtaining services (each, a &quot;Service User&quot;) through the App (such
            services, collectively, the &quot;Services&quot;). The Terms of Use described below incorporate the Privacy Policy and apply to all users of the App, including users who are also
            contributors of text, photo &amp; video content, information, private and public messages, advertisements, and other materials or Services on the App.
          </p>

          <p className="font-semibold">The App is currently owned and operated by KidEx Venture Private Limited.</p>

          <p>
            You acknowledge that the App serves as a venue for the online distribution and publication of user &amp; company submitted information between Service Providers and Service Users, and, by
            using, visiting, registering for, and/or otherwise participating in this App, including the Services presented, promoted, and displayed on the App, and by clicking on &quot;I have read and
            agree to the terms of use,&quot; you hereby certify that: (1) you are either a Service Provider or a prospective Service User, (2) you have the authority to enter into these Terms of Use,
            (3) you authorize the transfer of payment for Services requested through the use of the App, and (4) you agree to be bound by all terms and conditions of these Terms of Use and any other
            documents incorporated by reference herein.
          </p>

          <p>You specifically agree that by using the app, you are at least 18 years of age and you are competent under law to enter into a legally binding and enforceable contract.</p>

          <p>
            All references to &quot;you&quot; or &quot;your,&quot; as applicable, mean the person that accesses, uses, and/or participates in the App in any manner. If you use the App or open an
            Account on behalf of a business, you represent and warrant that you have the authority to bind that business and your acceptance of the Terms of Use will be deemed an acceptance by that
            business and &quot;you&quot; and &quot;your&quot; herein shall refer to that business.
          </p>

          <SubSection title="1. MODIFICATIONS TO TERMS OF USE AND/OR PRIVACY POLICY">
            <p>
              PlaceRep.ai reserves the right, in its sole discretion, to change, modify, or otherwise amend the Terms of Use, and any other documents incorporated by reference herein for complying
              with legal and regulatory framework and for other legitimate business purposes, at any time, and PlaceRep.ai will post notice of the changes and the amended Terms of Use at the domain of
              www.placerep.ai/privacy-policy. It is your responsibility to review the Terms of Use for any changes. Your use of the App following any amendment of the Terms of Use will signify your
              assent to and acceptance of any revised Terms of Use.
            </p>
          </SubSection>

          <SubSection title="2. PRIVACY POLICY">
            <p>
              PlaceRep.ai has established a Privacy Policy that explains to users how their information is collected and used. The Privacy Policy is referenced above and hereby incorporated into the
              Terms of Use set forth herein. Your use of this App is governed by the Privacy Policy.
            </p>
            <p>The Privacy Policy is located at: www.placerep.ai/privacy-policy.</p>
          </SubSection>
        </Section>

        {/* Membership and Accessibility */}
        <Section title="Membership and Accessibility">
          <SubSection title="1. LICENSE TO ACCESS">
            <p>
              PlaceRep.ai hereby grants you a non-exclusive, revocable license to use the App as set forth in the Terms of Use; provided, however, that (i) you will not copy, distribute, or make
              derivative works of the App in any medium without PlaceRep.ai&apos;s prior written consent; (ii) you will not alter or modify any part of the App other than as may be reasonably
              necessary to use the App for its intended purposes; and (iii) you will otherwise act in accordance with the terms and conditions of the Terms of Use and in accordance with all applicable
              laws.
            </p>
          </SubSection>

          <SubSection title="2. MEMBERSHIP ELIGIBILITY CRITERIA">
            <p>
              Use of the App is available only to individuals who are at least 18 years old and can form legally binding contracts under applicable law. You represent, acknowledge and agree that you
              are at least 18 years of age, and that: (a) all registration information that you submit is truthful and accurate, (b) you will maintain the accuracy of such information, and (c) your
              use of the App and Services offered through this App do not violate any applicable law or regulation. Your Account may be terminated without warning if we at our discretion, believe that
              you are under the age of 18 or that you are not complying with any applicable laws, rules or regulations.
            </p>
            <p>
              You need not register with PlaceRep.ai to simply visit and view the App, but to access and participate in certain features of the App, you will need to create a password-protected
              account (&quot;Account&quot;). To create an account, you must submit your phone number through the account registration page on the App and receive a third party generated one-time
              password.
            </p>
            <p>
              You are solely responsible for safeguarding your PlaceRep.ai password at all times and shall keep your passwords secure at all times. You shall be solely responsible for all activity
              that occurs on your Account and you shall notify PlaceRep.ai immediately of any breach of security or any unauthorized use of your Account.
            </p>
            <p>
              You hereby expressly acknowledge and agree that you yourself and not PlaceRep.ai will be liable for your losses, damages etc. (whether direct or indirect) caused by an unauthorized use
              of your Account.
            </p>
          </SubSection>

          <SubSection title="Account Policies">
            <ul className="list-disc pl-5 space-y-2">
              <li>You will not copy or distribute any part of the App in any medium without PlaceRep.ai&apos;s prior written authorization.</li>
              <li>You will not alter or modify any part of the App other than as may be reasonably necessary to use the App for its intended purpose.</li>
              <li>You will provide true, accurate, current and complete information when creating your Account and you shall maintain and update such information.</li>
              <li>
                You shall not use any automated system, including but not limited to, &quot;robots,&quot; &quot;spiders,&quot; &quot;offline readers,&quot; &quot;scrapers,&quot; etc., to access the
                App for any purpose without PlaceRep.ai&apos;s prior written approval.
              </li>
              <li>
                You shall not recruit, solicit, or contact in any form Service Providers or Service Users for employment or contracting for a business not affiliated with PlaceRep.ai without express
                written permission from PlaceRep.ai.
              </li>
              <li>
                You agree not to collect or harvest any personally identifiable data from the App, nor to use the communication systems provided by the App for any commercial solicitation purposes.
              </li>
            </ul>
          </SubSection>

          <SubSection title="3. ADDITIONAL POLICIES">
            <p>
              Your access to, use of, and participation in the App is subject to the Terms of Use and all applicable PlaceRep.ai regulations, guidelines and additional policies that PlaceRep.ai may
              set forth from time to time. You hereby acknowledge and agree that if you fail to adhere to any of the terms and conditions of this Agreement, PlaceRep.ai, in its sole discretion, may
              terminate your Account at any time without prior notice to you as well as initiate appropriate legal proceedings, if necessary.
            </p>
          </SubSection>
        </Section>

        {/* Member Conduct */}
        <Section title="Member Conduct">
          <SubSection title="1. PROHIBITIONS ON SUBMITTED CONTENT">
            <p>
              You shall not upload, post, transmit, transfer, disseminate, distribute, or facilitate distribution of any content, including text, images, video, sound, data, information, or software,
              to any part of the App that:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Misrepresents the source of anything you post, including impersonation of another individual or entity or any false or inaccurate biographical information;</li>
              <li>Invades anyone&apos;s privacy by attempting to harvest, collect, store, or publish private or personally identifiable information without their knowledge and willing consent;</li>
              <li>Contains falsehoods or misrepresentations that could damage PlaceRep.ai or any third party;</li>
              <li>Is pornographic, harassing, hateful, illegal, obscene, defamatory, libelous, slanderous, threatening, discriminatory, racially, culturally or ethnically offensive;</li>
              <li>Is copyrighted, protected by trade secret or otherwise subject to third-party proprietary rights, unless you are the owner of such rights;</li>
              <li>Is intended to threaten, stalk, defame, defraud, degrade, victimize, or intimidate an individual or group of individuals for any reason;</li>
              <li>Intends to harm or disrupt another user&apos;s computer or would allow others to illegally access software or bypass security;</li>
              <li>Advertises or solicits a business not related to or appropriate for the App;</li>
              <li>Contains or could be considered &quot;junk mail,&quot; &quot;spam,&quot; &quot;chain letters,&quot; &quot;pyramid schemes,&quot; or unsolicited commercial advertisement;</li>
              <li>Distributes or contains viruses or any other technologies that may harm PlaceRep.ai, or the interests or property of PlaceRep.ai users;</li>
            </ul>
          </SubSection>

          <SubSection title="2. PROHIBITIONS ON SENDING MESSAGES">
            <p>You will not send messages to other users containing:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Offers to make national or international money transfers for amounts exceeding the asking price of a service, with intent to request a refund of any portion of the payment;</li>
              <li>Unsolicited advertising or marketing of a service not offered on the App or an external app.</li>
            </ul>
          </SubSection>

          <SubSection title="3. NO DISCRIMINATION">
            <p>
              Indian laws prohibit any preference, limitation or discrimination based on race, color, religion, sex, national origin, age, handicap or other protected class. PlaceRep.ai will not
              knowingly accept any Posting which is in violation of the law. PlaceRep.ai has the right, in its sole discretion and without prior notice to you, to immediately remove any posting that
              discriminates or is any way in violation of any law.
            </p>
          </SubSection>

          <SubSection title="4. FEEDBACK">
            <p>
              As a participant in the App, you agree to use careful, prudent, and good judgment when leaving Feedback for another user. The following actions constitute inappropriate uses of Feedback:
              (a) threatening to leave negative or impartial Feedback for another user unless that user provides services not included in the original Posting; (b) leaving Feedback in order to make a
              Service Provider or Service User appear better than they actually are; and (c) including conditions in an Offer or Want that restrict a user from leaving Feedback.
            </p>
          </SubSection>
        </Section>

        {/* Use of Submitted Content */}
        <Section title="Use of Submitted Content">
          <SubSection title="1. NO CONFIDENTIALITY">
            <p>
              The App may now or in the future permit the submission of videos or other communications submitted by you and other users. You understand that whether or not such Submitted Content is
              published, PlaceRep.ai does not guarantee any confidentiality with respect to any Submitted Content.
            </p>
          </SubSection>

          <SubSection title="2. YOUR REPRESENTATIONS AND WARRANTIES">
            <p>
              You shall be solely responsible for your own Submitted Content and the consequences of posting or publishing it. In connection with Submitted Content, you affirm, represent, and/or
              warrant that: (a) you own or have the necessary licenses, rights, consents, and permissions to use and authorize PlaceRep.ai to use all patent, trademark, trade secret, copyright or
              other proprietary rights in and to any and all Submitted Content; and (b) you have the written consent, release, and/or permission of each and every identifiable individual person in the
              Submitted Content.
            </p>
          </SubSection>

          <SubSection title="3. YOUR OWNERSHIP RIGHTS AND LICENSE TO PlaceRep.ai">
            <p>
              You retain all of your ownership rights in your Submitted Content. However, by submitting the Submitted Content to PlaceRep.ai for posting on the App, you hereby grant PlaceRep.ai a
              perpetual, worldwide, non-exclusive, royalty-free, sublicensable and transferable license to link to, use, reproduce, distribute, reformat, translate, prepare derivative works of,
              display, and perform the Submitted Content in connection with the App and PlaceRep.ai&apos;s business operations.
            </p>
            <p>You may remove your Submitted Content from the App at any time. If you choose to remove your Submitted Content, the license granted above will automatically expire.</p>
          </SubSection>

          <SubSection title="4. PlaceRep.ai'S DISCLAIMERS AND RIGHT TO REMOVE">
            <p>
              PlaceRep.ai does not endorse any Submitted Content or any opinion, recommendation, or advice expressed therein, and PlaceRep.ai expressly disclaims any and all liability in connection
              with all Submitted Content. PlaceRep.ai does not permit copyright infringing activities and infringement of intellectual property rights on the App, and PlaceRep.ai will remove any Data
              or Submitted Content if properly notified that such content infringes on another&apos;s intellectual property rights.
            </p>
          </SubSection>

          <SubSection title="5. SUGGESTIONS">
            <p>
              If you send or transmit any communications, comments, questions, suggestions, or related materials to PlaceRep.ai (&quot;Suggestions&quot;), all such Suggestions are, and will be treated
              as, non-confidential and non-proprietary. You hereby assign all right, title, and interest in, and PlaceRep.ai is free to use, without any attribution or compensation to you, any ideas,
              know-how, concepts, techniques, or other intellectual property and proprietary rights contained in the Suggestions.
            </p>
          </SubSection>
        </Section>

        {/* Copyright Infringement */}
        <Section title="Copyright Infringement Take Down Procedure">
          <p>
            PlaceRep.ai has high regard for intellectual property and expects the same level of standard to be employed by its users. If you believe that your work has been copied and posted on the
            App in a way that constitutes copyright infringement and/or trademark infringement, please send the following information to us at info@kid-ex.com:
          </p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Identification of the copyrighted and/or trademarked work claimed to have been infringed;</li>
            <li>Identification of the material that is claimed to be infringing and information reasonably sufficient to permit PlaceRep.ai to locate the material;</li>
            <li>A written statement that you have a good faith belief that the disputed use is not authorized by the copyright and/or trademark owner, its agent, or the law;</li>
            <li>Information reasonably sufficient to permit PlaceRep.ai to contact you as the complaining party;</li>
            <li>An electronic or physical signature of the person authorized to act on behalf of the owner of an exclusive interest that is allegedly infringed;</li>
            <li>
              A statement by you, made under penalty of perjury, that the information in your report is accurate and that you are the owner of the exclusive right or authorized to act on the behalf of
              the owner.
            </li>
          </ol>
        </Section>

        {/* Modifications or Termination */}
        <Section title="Modifications to or Termination of App">
          <SubSection title="1. MODIFICATION OR CESSATION OF APP">
            <p>
              PlaceRep.ai reserves the right at any time and from time to time to modify or discontinue, temporarily or permanently, the App (or any part thereof) with or without notice and in its
              sole discretion. You agree that PlaceRep.ai shall not be liable to you or to any third party for any modification, suspension or discontinuance of PlaceRep.ai services.
            </p>
          </SubSection>

          <SubSection title="2. TERMINATION BY PlaceRep.ai">
            <p>
              You hereby acknowledge and agree that PlaceRep.ai, in its sole and absolute discretion, has the right (but not the obligation) to delete, terminate, or deactivate your Account, block
              your email or IP address, cancel the App or otherwise terminate your access to or participation in the use of the App, immediately and without notice, for any reason, including without
              limitation, Account inactivity or if PlaceRep.ai believes or has reason to believe that you have violated any provision of the Terms of Use.
            </p>
          </SubSection>

          <SubSection title="3. TERMINATION BY YOU">
            <p>You may cancel your use of the App and/or terminate the Terms of Use with or without cause at any time by writing to info@kid-ex.com.</p>
          </SubSection>

          <SubSection title="4. EFFECT OF TERMINATION">
            <p>
              Upon termination of your Account, your right to participate in the App shall automatically terminate. You acknowledge and agree that your right to receive any fees or compensation
              hereunder is conditional upon your proper use of the App, your adherence to the Terms of Use, the continuous activation of your Account, and your permitted participation in the App. In
              the event of Termination of Service, your Account will be disabled and you may not be granted access to your Account or any files or other data contained in your Account.
            </p>
          </SubSection>
        </Section>

        {/* Intellectual Property */}
        <Section title="Intellectual Property Rights">
          <SubSection title="1. PlaceRep.ai OWNS OR HOLDS THE LICENSES TO ALL DATA AND MARKS ON THE APP">
            <p>
              The content on the App (exclusive of all Submitted Content), including without limitation, the text, software, scripts, graphics, photos, sounds, music, videos, interactive features and
              the like (&quot;Data&quot;) and the trademarks, service marks and logos contained therein (&quot;Marks&quot;), are owned by PlaceRep.ai. Other trademarks, names and logos on this App are
              the property of their respective owners.
            </p>
            <p>
              Data on the App is provided to you AS IS for your information and personal use only and may not be used, copied, reproduced, distributed, transmitted, broadcast, displayed, sold,
              licensed, or otherwise exploited for any other purposes whatsoever without the prior written consent of the respective owners.
            </p>
          </SubSection>

          <SubSection title="2. PlaceRep.ai'S LICENSE TO YOU FOR THE USE OF DATA AND MARKS">
            <p>
              Subject to these Terms of Use, PlaceRep.ai hereby grants you a limited, revocable, nontransferable, nonsublicensable license to reproduce and display the PlaceRep.ai Data (excluding any
              software source code) solely for your personal use in connection with accessing and participating in the App.
            </p>
          </SubSection>
        </Section>

        {/* Fees */}
        <Section title="PlaceRep.ai Fees">
          <SubSection title="1. FEES">
            <p>
              Joining PlaceRep.ai, opening an Account, posting Services and viewing posted Services is free. PlaceRep.ai reserves the right at its sole discretion to charge fees for other services
              that PlaceRep.ai may provide in the future.
            </p>
          </SubSection>

          <SubSection title="2. TAXES">
            <p>
              You understand that we are acting solely as an intermediary for the collection of fees between Service Users and Service Providers who choose to enter into an Agreement for Service. You
              understand and agree that you are solely responsible for determining your own tax reporting requirements in consultation with tax advisors.
            </p>
          </SubSection>

          <SubSection title="3. REFUND POLICY">
            <p>All commissions payable to PlaceRep.ai on consummated transactions are final and non-refundable.</p>
          </SubSection>
        </Section>

        {/* Dispute Resolution */}
        <Section title="Dispute Resolution">
          <p>
            If a dispute arises between you and PlaceRep.ai, our goal is to provide you with a neutral and cost effective means of resolving the dispute quickly. Before resorting to the filing of a
            formal lawsuit, we strongly encourage you to first contact us directly to seek a resolution via e-mail at info@kid-ex.com.
          </p>

          <SubSection title="GOVERNING LAW">
            <p>
              The Terms of Use shall be governed in all respects by the laws of India and any legal proceeding arising out of this Agreement will occur exclusively in the courts located in Guwahati,
              Assam, India.
            </p>
          </SubSection>
        </Section>

        {/* Disclaimer */}
        <Section title="Disclaimer of Warranties">
          <p className="uppercase text-xs">
            YOU AGREE THAT YOUR USE OF THE APP SHALL BE AT YOUR OWN RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, PlaceRep.ai AND ITS OFFICERS, MANAGERS, MEMBERS, DIRECTORS, EMPLOYEES, SUCCESSORS,
            ASSIGNS, SUBSIDIARIES, AFFILIATES, SUPPLIERS, AND AGENTS DISCLAIM ALL WARRANTIES, EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, AND MAKE NO WARRANTIES, REPRESENTATIONS, OR GUARANTEES IN
            CONNECTION WITH THIS APP, THE SERVICES OFFERED ON OR THROUGH THIS APP, ANY DATA, MATERIALS, SUBMITTED CONTENT, RELATING TO THE QUALITY, SUITABILITY, TRUTH, ACCURACY OR COMPLETENESS OF ANY
            INFORMATION OR MATERIAL CONTAINED OR PRESENTED ON THIS APP. THIS APP, THE SERVICES OFFERED ON OR THROUGH THIS APP, AND ANY INFORMATION OR MATERIAL CONTAINED OR PRESENTED ON THIS APP IS
            PROVIDED TO YOU ON AN &quot;AS IS,&quot; &quot;AS AVAILABLE&quot; AND &quot;WHERE IS&quot; BASIS WITH NO WARRANTY OF IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            OR NON-INFRINGEMENT OF THIRD-PARTY RIGHTS.
          </p>
        </Section>

        {/* Limitations */}
        <Section title="Limitations of Liability">
          <p className="uppercase text-xs">
            IN NO EVENT SHALL PlaceRep.ai, OR ITS RESPECTIVE OFFICERS, MANAGERS, MEMBERS, DIRECTORS, EMPLOYEES, SUCCESSORS, ASSIGNS, SUBSIDIARIES, AFFILIATES, SUPPLIERS, ATTORNEYS OR AGENTS, BE LIABLE
            TO YOU FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, CONSEQUENTIAL OR EXEMPLARY DAMAGES WHATSOEVER RESULTING FROM ANY ACCESS TO OR USE OF THE APP OR ANY SERVICES, ERRORS,
            MISTAKES, OR INACCURACIES OF DATA, UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS, ANY BUGS, VIRUSES, OR THE LIKE WHICH MAY BE TRANSMITTED TO OR THROUGH THE APP, ANY INTERRUPTION OR
            CESSATION OF TRANSMISSION, OR ANY USE OF ANY DATA, CONTENT, INFORMATION, OR SUBMITTED CONTENT MADE AVAILABLE VIA THE APP, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE),
            OR ANY OTHER LEGAL THEORY.
          </p>

          <SubSection title="FORCE MAJEURE">
            <p>
              Neither PlaceRep.ai nor you shall be liable to the other for any delay or failure in performance under the Terms of Use, other than payment obligations, arising out of a cause beyond its
              control and without its fault or negligence. Such causes may include, but are not limited to fires, floods, earthquakes, strikes, unavailability of necessary utilities, blackouts, acts
              of God, acts of declared or undeclared war, acts of regulatory agencies, or national disasters.
            </p>
          </SubSection>
        </Section>

        {/* Indemnification */}
        <Section title="Indemnification and Release">
          <p className="uppercase text-xs">
            YOU AGREE TO DEFEND, INDEMNIFY AND HOLD HARMLESS PlaceRep.ai, AND ITS OFFICERS, MANAGERS, MEMBERS, DIRECTORS, EMPLOYEES, SUCCESSORS, ASSIGNS, SUBSIDIARIES, AFFILIATES, SUPPLIERS, AND
            AGENTS, FROM AND AGAINST ANY AND ALL CLAIMS, DAMAGES, OBLIGATIONS, LOSSES, LIABILITIES, COSTS OR DEBT, AND EXPENSES ARISING FROM YOUR USE OF, ACCESS TO, AND PARTICIPATION IN THE APP; YOUR
            VIOLATION OF ANY PROVISION OF THE TERMS OF USE; YOUR VIOLATION OF ANY THIRD-PARTY RIGHT; OR ANY CLAIM THAT YOUR SUBMITTED CONTENT CAUSED DAMAGE TO A THIRD PARTY. THIS DEFENSE AND
            INDEMNIFICATION OBLIGATION WILL SURVIVE THESE TERMS OF SERVICE AND YOUR USE OF THE APP.
          </p>
          <p className="uppercase text-xs">
            THE TERMS OF USE, AND ANY RIGHTS AND LICENSES GRANTED HEREUNDER, MAY NOT BE TRANSFERRED OR ASSIGNED BY YOU, BUT MAY BE ASSIGNED BY PlaceRep.ai WITHOUT RESTRICTION.
          </p>
        </Section>

        {/* No Third-party Beneficiaries */}
        <Section title="No Third-party Beneficiaries">
          <p>You agree that, except as otherwise expressly provided in the Terms of Use, there shall be no third-party beneficiaries to the Terms of Use.</p>
        </Section>

        {/* Notice */}
        <Section title="Notice">
          <p>You agree that PlaceRep.ai may provide you with notices, including those regarding changes to the Terms of Use, by email, regular mail, or postings on the App.</p>
        </Section>

        {/* General Information */}
        <Section title="General Information">
          <SubSection title="1. ENTIRE TERMS OF USE">
            <p>
              The Terms of Use, together with the Privacy Policy and any other legal notices or Additional Policies published by PlaceRep.ai on the App, shall constitute the entire agreement between
              you and PlaceRep.ai concerning the App. If any provision of the Terms of Use is deemed invalid by a court of competent jurisdiction, the invalidity of such provision shall not affect the
              validity of the remaining provisions of the Terms of Use, which shall remain in full force and effect.
            </p>
          </SubSection>

          <SubSection title="2. STATUTE OF LIMITATIONS">
            <p>
              You agree that any cause of action arising out of or related to the App must commence within one (1) year after the cause of action accrues. Otherwise, such cause of action is
              permanently barred.
            </p>
          </SubSection>

          <SubSection title="3. SECTION HEADINGS">
            <p>The section headings in the Terms of Use are for convenience only and have no legal or contractual effect.</p>
          </SubSection>
        </Section>
      </div>
    </div>
  )
}
